import { getFirestore, doc, getDoc } from "firebase/firestore";

import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInWithPopup, GoogleAuthProvider } from "firebase/auth";

import { getStorage } from "firebase/storage";

import app from "./firebaseConfig"; // Firebase app configuration

// Initialize Firestore, Authentication, and Storage
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);

/**
 * Logs in a user with email and password.
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<Object>} - Firebase User object.
 */
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Error logging in with email:", error);
    throw error;
  }
};

/**
 * Logs in a user with Google using a popup.
 * @returns {Promise<Object>} - Firebase User object.
 */
export const loginWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account", // Forces account selection
  });

  try {
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  } catch (error) {
    console.error("Error logging in with Google:", error);
    throw error;
  }
};

/**
 * Logs out the current user.
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await signOut(auth);
    console.log("User logged out successfully.");
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

/**
 * Fetches the user's role from the Firestore database.
 * Assumes a "users" collection with each user's `uid` and `role`.
 * @param {string} uid - The user's unique Firebase ID.
 * @returns {Promise<string>} - User's role ("admin" or "user").
 */
export const getUserRole = async (uid) => {
  const userDoc = doc(db, "users", uid); // Reference the user's document in Firestore
  try {
    const userSnap = await getDoc(userDoc);

    if (userSnap.exists()) {
      return userSnap.data().role; // Assumes "role" field exists in the document
    } else {
      throw new Error("User role not found!");
    }
  } catch (error) {
    console.error("Error fetching user role:", error);
    throw error;
  }
};

/**
 * Adds a listener for authentication state changes.
 * @param {function} callback - Function to call when the auth state changes.
 * @returns {function} - Unsubscribe function to stop listening for auth state changes.
 */
export const onAuthStateChangedListener = (callback) => {
  const unsubscribe = onAuthStateChanged(auth, callback); // Firebase's onAuthStateChanged returns an unsubscribe function
  return unsubscribe; // Now this will return the unsubscribe function.
};
